import { useEffect, useMemo, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Field,
  FieldLabel,
  FieldError,
  FieldDescription,
  FieldSet,
  FieldLegend,
  FieldGroup,
} from "@/components/ui/field";
import { DatePicker } from "@/components/ui/date-picker";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Separator } from "@/components/ui/separator";
import { CalendarRange, CheckCircle2, ChevronDown, ChevronRight, Clock, Plus, Search, Truck, User } from "lucide-react";
import { LoadingAnimation } from "@/shared/components/LoadingAnimation";
import { useDebounce } from "@/shared/hooks/useDebounce";
import { normalizeError } from "@/shared/errors/normalizeError";
import {
  useDeliveryPlansPaged,
  useCreateDeliveryPlan,
  useCheckDeliveryPlanConflicts,
  useDeliveryPlanDetail,
  usePublishDeliveryPlan,
  useCancelDeliveryPlan,
  useReassignDeliveryPlan,
  useEligibleOrders,
  useUpdateDeliveryPlan,
} from "../hooks/useDeliveryPlans";
import { useDrivers } from "../hooks/useDrivers";
import { useVehicles } from "../hooks/useVehicles";
import { deliveryPlansApi } from "../services/deliveryPlansApi";
import { driverAvailabilityApi } from "../services/driverAvailabilityApi";
import { routeTemplatesApi } from "../services/routeTemplatesApi";
import { useQuery } from "@tanstack/react-query";
import type {
  CheckDeliveryPlanConflictsRequest,
  DeliveryPlanConflictDto,
  DeliveryPlanConflictCheckResultDto,
  DeliveryPlanListQuery,
  DeliveryPlanStopInput,
  DeliveryPlanStatus,
  DeliveryPlanType,
  DeliveryPlanSummaryDto,
  CreateDeliveryPlanRequest,
  PlannerEligibleOrderLineDto,
  PlannerEligibleOrderSummaryDto,
  ReassignDeliveryPlanRequest,
  UpdateDeliveryPlanRequest,
} from "../services/deliveryPlansApi";
import {
  clampPlanningQuantity,
  findAssignedVehicleId,
  formatOrderLinePackaging,
  formatQuantityBreakdown,
  getOrderAddRestriction,
  getPlanStructuralEditRestriction,
  getStopEtaRestriction,
  groupOrdersByStop,
  isVehicleAvailableForPlanning,
} from "./deliveryPlanUx";

// ── Helpers ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS: { value: DeliveryPlanStatus; label: string }[] = [
  { value: "Draft", label: "Draft" },
  { value: "Scheduled", label: "Scheduled" },
  { value: "Published", label: "Published" },
  { value: "DriverAccepted", label: "Driver Accepted" },
  { value: "InProgress", label: "In Progress" },
  { value: "Completed", label: "Completed" },
  { value: "PartiallyCompleted", label: "Partially Completed" },
  { value: "Cancelled", label: "Cancelled" },
  { value: "ReconciliationRequired", label: "Reconciliation Required" },
];

const PLAN_TYPE_OPTIONS: { value: DeliveryPlanType; label: string }[] = [
  { value: "OrderDelivery", label: "Order Delivery" },
  { value: "ExpoDelivery", label: "Expo Delivery" },
  { value: "TrayDispatch", label: "Tray Dispatch" },
  { value: "TrayCollection", label: "Tray Collection" },
  { value: "MixedDelivery", label: "Mixed Delivery" },
];

const TIME_SLOT_OPTIONS: { value: string; label: string }[] = Array.from({ length: 96 }, (_, index) => {
  const hour24 = Math.floor(index / 4);
  const minute = (index % 4) * 15;
  const value = `${hour24.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  const hour12 = ((hour24 + 11) % 12) + 1;
  const meridiem = hour24 < 12 ? "AM" : "PM";
  const label = `${hour12.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")} ${meridiem}`;

  return { value, label };
});

const statusBadge = (status: DeliveryPlanStatus) => {
  const map: Record<DeliveryPlanStatus, { variant: "default" | "secondary" | "outline" | "destructive"; label: string }> = {
    Draft: { variant: "secondary", label: "Draft" },
    Scheduled: { variant: "outline", label: "Scheduled" },
    Published: { variant: "default", label: "Published" },
    DriverAccepted: { variant: "default", label: "Accepted" },
    InProgress: { variant: "default", label: "In Progress" },
    Completed: { variant: "secondary", label: "Completed" },
    PartiallyCompleted: { variant: "outline", label: "Partial" },
    Cancelled: { variant: "destructive", label: "Cancelled" },
    ReconciliationRequired: { variant: "destructive", label: "Reconciliation" },
  };
  const b = map[status] ?? { variant: "secondary" as const, label: status };
  return <Badge variant={b.variant}>{b.label}</Badge>;
};

function formatDate(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-KE", { day: "numeric", month: "short", year: "numeric" });
}

function formatTime(iso?: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleTimeString("en-KE", { hour: "2-digit", minute: "2-digit" });
}

function formatCurrency(amount: number): string {
  return `KES ${amount.toLocaleString("en-KE", { minimumFractionDigits: 0 })}`;
}

function formatQuantity(amount: number | undefined): string {
  return (amount ?? 0).toLocaleString("en-KE", { maximumFractionDigits: 2 });
}

// ── Page ──────────────────────────────────────────────────────────────────

export function DeliveryPlansPage() {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [planTypeFilter, setPlanTypeFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [showCreate, setShowCreate] = useState(false);
  const [initialOrder, setInitialOrder] = useState<PlannerEligibleOrderSummaryDto | undefined>();
  const [editPlanId, setEditPlanId] = useState<string | null>(null);
  const [viewPlan, setViewPlan] = useState<DeliveryPlanSummaryDto | null>(null);
  const [reassignPlan, setReassignPlan] = useState<DeliveryPlanSummaryDto | null>(null);
  const [cancelPlan, setCancelPlan] = useState<DeliveryPlanSummaryDto | null>(null);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [isPreparingCancellation, setIsPreparingCancellation] = useState(false);

  const [eligibleSearch, setEligibleSearch] = useState("");
  const debouncedEligibleSearch = useDebounce(eligibleSearch.trim(), 300);
  const [eligiblePage, setEligiblePage] = useState(1);
  const eligiblePageSize = 10;

  const eligibleOrderQuery = useMemo(
    () => ({
      search: debouncedEligibleSearch || undefined,
      pageNumber: eligiblePage,
      pageSize: eligiblePageSize,
    }),
    [debouncedEligibleSearch, eligiblePage]
  );
  const {
    data: eligibleOrderResult,
    isLoading: eligibleOrdersLoading,
    isFetching: eligibleOrdersFetching,
    error: eligibleOrdersError,
  } = useEligibleOrders(eligibleOrderQuery);
  const eligibleOrders = eligibleOrderResult?.items ?? [];
  const eligibleOrderCount = eligibleOrderResult?.totalCount ?? 0;
  const eligibleTotalPages = eligibleOrderResult?.totalPages ?? 0;

  const query: DeliveryPlanListQuery = {
    search: search || undefined,
    status: statusFilter === "all" ? undefined : (statusFilter as DeliveryPlanStatus),
    planType: planTypeFilter === "all" ? undefined : (planTypeFilter as DeliveryPlanType),
    pageNumber: page,
    pageSize,
  };

  const { data, isLoading, error, isFetching } = useDeliveryPlansPaged(query);
  const plans = data?.items ?? [];
  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const publishMutation = usePublishDeliveryPlan();
  const cancelMutation = useCancelDeliveryPlan();

  const handlePublish = (plan: DeliveryPlanSummaryDto) => {
    if (plan.status !== "Scheduled" && plan.status !== "Draft") return;
    publishMutation.mutate({ id: plan.id, data: {} });
  };

  const openCancelDialog = (plan: DeliveryPlanSummaryDto) => {
    setCancelPlan(plan);
    setCancelReason("");
    setCancelError(null);
  };

  const closeCancelDialog = () => {
    if (isPreparingCancellation || cancelMutation.isPending) return;
    setCancelPlan(null);
    setCancelReason("");
    setCancelError(null);
  };

  const handleCancel = async () => {
    const reason = cancelReason.trim();
    if (!cancelPlan || !reason) return;

    setCancelError(null);
    setIsPreparingCancellation(true);
    try {
      const detail = await deliveryPlansApi.getById(cancelPlan.id);
      await cancelMutation.mutateAsync({
        id: cancelPlan.id,
        data: { reason, rowVersion: detail.rowVersion },
      });
      setCancelPlan(null);
      setCancelReason("");
    } catch (error) {
      setCancelError(normalizeError(error).userMessage || "Failed to cancel delivery plan.");
    } finally {
      setIsPreparingCancellation(false);
    }
  };

  const openCreate = (order?: PlannerEligibleOrderSummaryDto) => {
    setInitialOrder(order);
    setShowCreate(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <CalendarRange className="w-8 h-8 text-primary" />
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Transport & Delivery Planner</h1>
            <p className="text-gray-500 mt-0.5">
              Plan and schedule deliveries with route, driver, and vehicle assignments.
            </p>
          </div>
        </div>
        <Button onClick={() => openCreate()}>
          <Plus className="w-4 h-4 mr-1" /> New Plan
        </Button>
      </div>

      {/* Orders ready for planning */}
      <section className="rounded-lg border bg-card">
        <div className="flex flex-col gap-4 border-b p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-emerald-500/10 p-2 text-emerald-600 dark:text-emerald-400">
              <CheckCircle2 className="h-5 w-5" />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="font-semibold">Orders ready to plan</h2>
                {!eligibleOrdersLoading && (
                  <Badge variant="secondary">
                    {eligibleOrderCount} eligible
                  </Badge>
                )}
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Orders in Ready for Release with quantities available for delivery planning.
              </p>
            </div>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={eligibleSearch}
              onChange={(event) => {
                setEligibleSearch(event.target.value);
                setEligiblePage(1);
              }}
              placeholder="Search eligible orders..."
              className="pl-9"
              aria-label="Search eligible orders"
            />
          </div>
        </div>

        {eligibleOrdersLoading ? (
          <div className="p-6"><LoadingAnimation /></div>
        ) : eligibleOrdersError ? (
          <p className="p-4 text-sm text-destructive">Failed to load eligible orders.</p>
        ) : eligibleOrders.length === 0 ? (
          <div className="p-6 text-center">
            <p className="font-medium text-muted-foreground">
              {debouncedEligibleSearch ? "No eligible orders match your search" : "No orders are ready to plan"}
            </p>
            <p className="mt-1 text-sm text-muted-foreground">
              {debouncedEligibleSearch
                ? "Try another order number, customer, or delivery location."
                : "Orders will appear here when they reach Ready for Release."}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Order</TableHead>
                    <TableHead>Customer</TableHead>
                    <TableHead>Route / stop</TableHead>
                    <TableHead>Ready items</TableHead>
                    <TableHead>Expected delivery</TableHead>
                    <TableHead className="text-right">Available to plan</TableHead>
                    <TableHead><span className="sr-only">Action</span></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {eligibleOrders.map((order) => (
                    <TableRow key={order.orderId}>
                      <TableCell className="font-medium">{order.orderNumber}</TableCell>
                      <TableCell>{order.customerName}</TableCell>
                      <TableCell className="max-w-64">
                        <span className="block truncate font-medium">{order.routeName ?? "Route not yet assigned"}</span>
                        <span className="block truncate text-xs text-muted-foreground">{(order.routeStopName ?? order.deliveryLocation) || "—"}</span>
                      </TableCell>
                      <TableCell className="max-w-72">
                        <span className="line-clamp-2 text-sm text-muted-foreground">
                          {(order.lines ?? []).map((line) =>
                            `${line.productName}: ${formatQuantity(line.availableToPlan)} ${formatOrderLinePackaging(line)}`
                          ).join(", ") || "—"}
                        </span>
                      </TableCell>
                      <TableCell className="whitespace-nowrap">{formatDate(order.expectedDeliveryDate)}</TableCell>
                      <TableCell className="text-right font-medium">
                        {formatQuantity(order.availableToPlan)}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="sm" variant="outline" onClick={() => openCreate(order)}>
                          Plan order
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
            <div className="flex flex-col gap-3 border-t px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between">
              <p className="text-muted-foreground">
                Showing {(eligiblePage - 1) * eligiblePageSize + 1}–{Math.min(eligiblePage * eligiblePageSize, eligibleOrderCount)} of {eligibleOrderCount}
                {eligibleOrdersFetching ? " · Refreshing..." : ""}
              </p>
              {eligibleTotalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={eligiblePage <= 1 || eligibleOrdersFetching}
                    onClick={() => setEligiblePage((current) => current - 1)}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={eligiblePage >= eligibleTotalPages || eligibleOrdersFetching}
                    onClick={() => setEligiblePage((current) => current + 1)}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </section>

      <div>
        <h2 className="text-lg font-semibold">Delivery plans</h2>
        <p className="text-sm text-muted-foreground">Existing and upcoming transport plans.</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search plans..."
            className="pl-9"
          />
        </div>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUS_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={planTypeFilter} onValueChange={(v) => { setPlanTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All types</SelectItem>
            {PLAN_TYPE_OPTIONS.map((t) => (
              <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>

        <span className="ml-auto text-sm text-muted-foreground">
          {isFetching ? "Loading..." : `${totalCount} plan${totalCount !== 1 ? "s" : ""}`}
        </span>
      </div>

      {/* Table */}
      {isLoading ? (
        <LoadingAnimation />
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <p className="text-red-600">Failed to load delivery plans.</p>
        </div>
      ) : plans.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <CalendarRange className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
          <p className="font-medium text-muted-foreground">No delivery plans found</p>
          <p className="text-sm text-muted-foreground mt-1">
            Create a new delivery plan to schedule routes, assign drivers, and set delivery times.
          </p>
        </div>
      ) : (
        <>
          <div className="rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Route</TableHead>
                  <TableHead>Driver</TableHead>
                  <TableHead>Vehicle</TableHead>
                  <TableHead>Departure</TableHead>
                  <TableHead>Stops</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {plans.map((plan) => (
                  <TableRow key={plan.id}>
                    <TableCell className="font-medium">{plan.name}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {PLAN_TYPE_OPTIONS.find((t) => t.value === plan.planType)?.label ?? plan.planType}
                    </TableCell>
                    <TableCell>{plan.routeName}</TableCell>
                    <TableCell>
                      <div className="space-y-1">
                        <div>{plan.driverName}</div>
                        {plan.plannedDriverMobileReadiness === "Ready" ? (
                          <Badge variant="outline" className="text-green-700 border-green-300">Mobile ready</Badge>
                        ) : plan.plannedDriverMobileReadiness === "Inactive" ? (
                          <Badge variant="destructive">Mobile account inactive</Badge>
                        ) : (
                          <Badge variant="secondary">Mobile account missing</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{plan.vehicleRegistrationNumber}</TableCell>
                    <TableCell className="whitespace-nowrap">
                      {formatDate(plan.plannedDepartureTime)}{" "}
                      <span className="text-muted-foreground">{formatTime(plan.plannedDepartureTime)}</span>
                    </TableCell>
                    <TableCell>{plan.stopCount}</TableCell>
                    <TableCell>{statusBadge(plan.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 justify-end">
                        <Button size="sm" variant="ghost" onClick={() => setViewPlan(plan)}>
                          View
                        </Button>
                        {(plan.status === "Draft" || plan.status === "Scheduled") && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handlePublish(plan)}
                            disabled={publishMutation.isPending}
                          >
                            Publish
                          </Button>
                        )}
                        {(plan.status === "Draft" || plan.status === "Scheduled") && (
                          <Button size="sm" variant="outline" onClick={() => setEditPlanId(plan.id)}>
                            Edit
                          </Button>
                        )}
                        {(plan.status === "Published" || plan.status === "DriverAccepted" || plan.status === "InProgress") && (
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => setReassignPlan(plan)}
                          >
                            Reassign
                          </Button>
                        )}
                        {!["Cancelled", "Completed", "PartiallyCompleted"].includes(plan.status) && (
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => openCancelDialog(plan)}
                            disabled={cancelMutation.isPending}
                          >
                            Cancel
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages}
              </p>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  Previous
                </Button>
                <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Create Dialog */}
      {showCreate && (
        <CreateDeliveryPlanDialog
          open={showCreate}
          initialOrder={initialOrder}
          onClose={() => {
            setShowCreate(false);
            setInitialOrder(undefined);
          }}
        />
      )}

      {editPlanId && (
        <CreateDeliveryPlanDialog
          open={!!editPlanId}
          onClose={() => setEditPlanId(null)}
          planId={editPlanId}
        />
      )}

      {viewPlan && (
        <DeliveryPlanDestinationsDialog
          plan={viewPlan}
          open={!!viewPlan}
          onClose={() => setViewPlan(null)}
        />
      )}

      {reassignPlan && (
        <ReassignDeliveryPlanDialog
          plan={reassignPlan}
          open={!!reassignPlan}
          onClose={() => setReassignPlan(null)}
        />
      )}

      <Dialog open={!!cancelPlan} onOpenChange={(open) => { if (!open) closeCancelDialog(); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Cancel Delivery Plan</DialogTitle>
            <DialogDescription>
              Enter a reason for cancelling <strong>{cancelPlan?.name}</strong>. This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <Field>
            <FieldLabel>
              Cancellation reason <span className="text-destructive">*</span>
            </FieldLabel>
            <Textarea
              value={cancelReason}
              onChange={(event) => {
                setCancelReason(event.target.value);
                if (cancelError) setCancelError(null);
              }}
              placeholder="Explain why this delivery plan is being cancelled"
              rows={4}
              maxLength={500}
              autoFocus
              disabled={isPreparingCancellation || cancelMutation.isPending}
            />
            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>A reason is required.</span>
              <span>{cancelReason.length}/500</span>
            </div>
          </Field>

          {cancelError && <FieldError>{cancelError}</FieldError>}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={closeCancelDialog}
              disabled={isPreparingCancellation || cancelMutation.isPending}
            >
              Keep Plan
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={() => void handleCancel()}
              disabled={!cancelReason.trim() || isPreparingCancellation || cancelMutation.isPending}
            >
              {isPreparingCancellation || cancelMutation.isPending ? "Cancelling..." : "Cancel Plan"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Validation ────────────────────────────────────────────────────────────

interface FormState {
  name: string;
  planType: DeliveryPlanType;
  routeId: string;
  driverId: string;
  vehicleId: string;
  departureDate: string;
  departureTime: string;
  driverOverrideReason: string;
  vehicleOverrideReason: string;
  notes: string;
}

interface FormErrors {
  name?: string;
  routeId?: string;
  driverId?: string;
  vehicleId?: string;
  departureDate?: string;
  departureTime?: string;
  stops?: string;
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
}

type SelectedPlannerOrderLine = PlannerEligibleOrderLineDto & {
  plannedQuantity: number;
};

type SelectedPlannerOrder = Omit<PlannerEligibleOrderSummaryDto, "lines"> & {
  lines: SelectedPlannerOrderLine[];
  stopExpectedDeliveryDate: string;
  stopExpectedDeliveryTime: string;
};

function combineDatetime(date: string, time: string): string {
  if (!date || !time) return "";
  return `${date}T${time}`;
}

function parseTimeTo24Hour(value: string): string | null {
  const raw = value.trim();
  if (!raw) return null;

  const hhmm = raw.match(/^([01]?\d|2[0-3]):([0-5]\d)$/);
  if (hhmm) {
    return `${hhmm[1].padStart(2, "0")}:${hhmm[2]}`;
  }

  const compact = raw.match(/^([01]\d|2[0-3])([0-5]\d)$/);
  if (compact) {
    return `${compact[1]}:${compact[2]}`;
  }

  const meridian = raw.match(/^(\d{1,2})(?::?([0-5]\d))?\s*([ap]m)$/i);
  if (meridian) {
    const hour12 = Number(meridian[1]);
    const minute = Number(meridian[2] ?? "0");
    if (Number.isNaN(hour12) || Number.isNaN(minute) || hour12 < 1 || hour12 > 12) {
      return null;
    }

    const isPm = meridian[3].toLowerCase() === "pm";
    const hour24 = (hour12 % 12) + (isPm ? 12 : 0);
    return `${hour24.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;
  }

  return null;
}

function normalizeTimeText(value: string): string {
  const parsed = parseTimeTo24Hour(value);
  return parsed ?? value.trim();
}

function conflictResultFromError(error: unknown): DeliveryPlanConflictCheckResultDto | null {
  const envelopeData = (error as { envelope?: { data?: unknown } })?.envelope?.data;
  if (!envelopeData || typeof envelopeData !== "object") return null;

  const raw = envelopeData as Record<string, unknown>;
  if (!Array.isArray(raw.conflicts)) return null;

  return {
    hasConflicts: Boolean(raw.hasConflicts),
    conflicts: raw.conflicts as DeliveryPlanConflictDto[],
    resolvedDeliveryFee: typeof raw.resolvedDeliveryFee === "number" ? raw.resolvedDeliveryFee : 0,
    resolvedDeliveryFeeSource:
      raw.resolvedDeliveryFeeSource === "RouteStop" ||
      raw.resolvedDeliveryFeeSource === "ManualOverride"
        ? raw.resolvedDeliveryFeeSource
        : "RouteTemplate",
  };
}

function validateForm(form: FormState, _stopCount: number): FormErrors {
  const errors: FormErrors = {};

  if (!form.name.trim()) errors.name = "Plan name is required.";
  else if (form.name.trim().length > 200) errors.name = "Plan name must be 200 characters or less.";

  const departureTime = parseTimeTo24Hour(form.departureTime);
  if (form.departureTime.trim() && !departureTime)
    errors.departureTime = "Enter time as HH:MM (24h) or with AM/PM.";

  if ((form.departureDate && !form.departureTime.trim()) || (!form.departureDate && form.departureTime.trim()))
    errors.departureTime = "Enter both the planned departure date and time, or leave both blank in Draft.";

  return errors;
}

interface ReassignFormState {
  driverId: string;
  vehicleId: string;
  departureDate: string;
  departureTime: string;
  reason: string;
  driverOverrideReason: string;
  vehicleOverrideReason: string;
  notes: string;
}

interface ReassignFormErrors {
  driverId?: string;
  vehicleId?: string;
  departureDate?: string;
  departureTime?: string;
  reason?: string;
  driverOverrideReason?: string;
  vehicleOverrideReason?: string;
}

function validateReassignForm(form: ReassignFormState): ReassignFormErrors {
  const errors: ReassignFormErrors = {};
  if (!form.driverId) errors.driverId = "Driver is required.";
  if (!form.vehicleId) errors.vehicleId = "Vehicle is required.";
  if (!form.departureDate) errors.departureDate = "Departure date is required.";
  if (!form.departureTime.trim()) errors.departureTime = "Departure time is required.";
  if (!form.reason.trim()) errors.reason = "Reassignment reason is required.";
  else if (form.reason.trim().length > 1000) errors.reason = "Reassignment reason must be 1000 characters or less.";
  const departureTime = parseTimeTo24Hour(form.departureTime);

  if (form.departureTime.trim() && !departureTime)
    errors.departureTime = "Enter time as HH:MM (24h) or with AM/PM.";
  return errors;
}

function isoToDateInput(value?: string) {
  if (!value) return "";
  return value.slice(0, 10);
}

function isoToTimeInput(value?: string) {
  if (!value) return "";
  return value.slice(11, 16);
}

// ── Create/Edit Dialog ────────────────────────────────────────────────────

function CreateDeliveryPlanDialog({
  open,
  onClose,
  planId,
  initialOrder,
}: {
  open: boolean;
  onClose: () => void;
  planId?: string;
  initialOrder?: PlannerEligibleOrderSummaryDto;
}) {
  const isEditMode = !!planId;
  const createMutation = useCreateDeliveryPlan();
  const updateMutation = useUpdateDeliveryPlan();
  const checkConflictsMutation = useCheckDeliveryPlanConflicts();
  const { data: detail } = useDeliveryPlanDetail(planId ?? "");
  const { data: drivers = [], isLoading: driversLoading } = useDrivers();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles();
  const { data: routes = [], isLoading: routesLoading } = useQuery({
    queryKey: ["route-templates-active"],
    queryFn: () => routeTemplatesApi.getActive(),
  });

  const today = new Date().toISOString().slice(0, 10);

  const [form, setForm] = useState<FormState>({
    name: "",
    planType: "OrderDelivery",
    routeId: "",
    driverId: "",
    vehicleId: "",
    departureDate: "",
    departureTime: "",
    driverOverrideReason: "",
    vehicleOverrideReason: "",
    notes: "",
  });

  const [eligibleSearch, setEligibleSearch] = useState("");
  const [eligiblePage, setEligiblePage] = useState(1);
  const [selectedLocation, setSelectedLocation] = useState("all");
  const [expandedEligibleOrderIds, setExpandedEligibleOrderIds] = useState<Set<string>>(new Set());
  const [notesOpen, setNotesOpen] = useState(false);
  const debouncedEligibleSearch = useDebounce(eligibleSearch.trim(), 300);
  const [selectedOrders, setSelectedOrders] = useState<SelectedPlannerOrder[]>(() =>
    initialOrder
      ? [{
          ...initialOrder,
          stopExpectedDeliveryDate: "",
          stopExpectedDeliveryTime: "",
          lines: (initialOrder.lines ?? [])
            .filter((line) => line.availableToPlan > 0)
            .map((line) => ({ ...line, plannedQuantity: line.availableToPlan })),
        }]
      : []
  );
  const [suggestingRoute, setSuggestingRoute] = useState(false);
  const [routeSuggestionMessage, setRouteSuggestionMessage] = useState<string | null>(null);
  const [preservedStops, setPreservedStops] = useState<DeliveryPlanStopInput[]>([]);
  const [conflictResult, setConflictResult] = useState<DeliveryPlanConflictCheckResultDto | null>(null);

  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [submitted, setSubmitted] = useState(false);

  const eligibleOrderQuery = useMemo(
    () => ({
      search: debouncedEligibleSearch || undefined,
      deliveryPlanId: planId,
      routeId: form.routeId || undefined,
      location: selectedLocation === "all" ? undefined : selectedLocation,
      pageNumber: eligiblePage,
      pageSize: 20,
    }),
    [debouncedEligibleSearch, eligiblePage, form.routeId, planId, selectedLocation]
  );

  const {
    data: eligibleOrderResult,
    isLoading: eligibleOrdersLoading,
    isFetching: eligibleOrdersFetching,
  } = useEligibleOrders(eligibleOrderQuery);
  const eligibleOrders = eligibleOrderResult?.items ?? [];
  const eligibleLocationCounts = eligibleOrderResult?.locationCounts ?? [];
  const eligibleTotalCount = eligibleOrderResult?.totalCount ?? 0;
  const eligibleTotalPages = eligibleOrderResult?.totalPages ?? 0;
  const allLocationOrderCount = eligibleLocationCounts.reduce((sum, item) => sum + item.orderCount, 0);
  const selectedRoute = routes.find((route) => route.id === form.routeId);
  const { data: selectedRouteDetail } = useQuery({
    queryKey: ["route-template-detail", form.routeId],
    queryFn: () => routeTemplatesApi.getById(form.routeId),
    enabled: !!form.routeId,
  });
  const resolvePlanningStop = (order: PlannerEligibleOrderSummaryDto | SelectedPlannerOrder) => {
    if (!selectedRoute) return undefined;
    if (order.routeStopId)
      return selectedRoute.stops.find((stop) => stop.id === order.routeStopId);
    if (!order.transportRateId) return undefined;
    const matches = selectedRoute.stops.filter((stop) => stop.transportRateId === order.transportRateId);
    return matches.length === 1 ? matches[0] : undefined;
  };

  useEffect(() => {
    if (!isEditMode || !detail) return;

    setForm({
      name: detail.name,
      planType: detail.planType,
      routeId: detail.routeId ?? "",
      driverId: detail.driverId ?? "",
      vehicleId: detail.vehicleId ?? "",
      departureDate: isoToDateInput(detail.plannedDepartureTime),
      departureTime: isoToTimeInput(detail.plannedDepartureTime),
      driverOverrideReason: "",
      vehicleOverrideReason: "",
      notes: detail.notes ?? "",
    });

    const orderStops = detail.stops
      .filter((s) => (s.items ?? []).length > 0)
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .flatMap((s) => {
        const itemsByOrder = new Map<string, typeof s.items>();
        (s.items ?? []).forEach((item) => {
          itemsByOrder.set(item.orderId, [...(itemsByOrder.get(item.orderId) ?? []), item]);
        });
        return [...itemsByOrder.entries()].map(([orderId, stopItems]) => {
        const lines = stopItems.map((item) => ({
          fulfilmentReleaseItemId: item.fulfilmentReleaseItemId ?? "",
          fulfilmentReleaseId: "",
          orderItemId: item.orderItemId,
          productName: item.productName,
          variety: item.variety,
          trayType: item.trayType,
          orderedQuantity: item.plannedQuantity,
          productionReadyQuantity: item.plannedQuantity,
          salesReleasedQuantity: item.plannedQuantity,
          deliveredQuantity: 0,
          alreadyPlannedQuantity: 0,
          availableToPlan: item.plannedQuantity,
          quantityPending: item.plannedQuantity,
          unit: item.unit,
          unitName: item.unitName,
          plannedQuantity: item.plannedQuantity,
        }));
        const plannedTotal = lines.reduce((sum, line) => sum + line.plannedQuantity, 0);
        return {
        orderId,
        orderNumber: stopItems[0]?.orderNumber || (s.orderId === orderId ? (s.orderNumber ?? "Order") : "Order"),
        customerId: s.customerId ?? "",
        customerName: stopItems[0]?.customerName || s.customerName || "Customer",
        deliveryLocation: stopItems[0]?.deliveryLocation || s.routePointName || "Route stop",
        expectedDeliveryDate: s.expectedDeliveryTime,
        stopExpectedDeliveryDate: isoToDateInput(s.expectedDeliveryTime),
        stopExpectedDeliveryTime: isoToTimeInput(s.expectedDeliveryTime),
        orderedQuantity: plannedTotal,
        deliveredQuantity: 0,
        alreadyPlannedQuantity: 0,
        availableToPlan: plannedTotal,
        remainingAfterCurrentPlan: plannedTotal,
        quantityPending: plannedTotal,
        totalAmount: 0,
        lines,
      };
        });
      });

    const nonOrderStops = detail.stops
      .filter((s) => (s.items ?? []).length === 0)
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .map((s) => ({
        customerId: s.customerId,
        routePointId: s.routePointId,
        collectionPointId: s.collectionPointId,
        plannedArrivalTime: s.plannedArrivalTime,
        expectedDeliveryTime: s.expectedDeliveryTime,
        notes: s.notes,
        sequenceNumber: s.sequenceNumber,
      }));

    setSelectedOrders(orderStops);
    setPreservedStops(nonOrderStops);
    setNotesOpen(!!detail.notes);
  }, [detail, isEditMode]);

  const set = (key: keyof FormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (key === "routeId") {
      setEligiblePage(1);
      setSelectedLocation("all");
    }
    if (key === "driverId" || key === "vehicleId" || key === "routeId" || key === "departureDate" || key === "departureTime" || key === "driverOverrideReason" || key === "vehicleOverrideReason") {
      setConflictResult(null);
    }
  };

  const touch = (key: string) => setTouched((prev) => new Set(prev).add(key));

  const errors = validateForm(form, selectedOrders.length + preservedStops.length);
  if (selectedOrders.some((order) => order.lines.every((line) => line.plannedQuantity <= 0))) {
    errors.stops = "Every selected order needs at least one planned line quantity.";
  }
  const normalizedDepartureTime = parseTimeTo24Hour(form.departureTime);
  const plannedDepartureForValidation = form.departureDate && normalizedDepartureTime
    ? combineDatetime(form.departureDate, normalizedDepartureTime)
    : undefined;
  const stopEtaRestriction = selectedOrders
    .map((order) => getStopEtaRestriction({
      date: order.stopExpectedDeliveryDate,
      time: order.stopExpectedDeliveryTime,
      plannedDeparture: plannedDepartureForValidation,
      orderExpectedDeliveries: [order.expectedDeliveryDate],
    }))
    .find((message) => !!message);
  if (stopEtaRestriction) errors.stops = stopEtaRestriction;
  const requiresVehicleOverride = conflictResult?.conflicts.some((c) => c.code === "VEHICLE_CONFLICT") ?? false;

  if (requiresVehicleOverride && !form.vehicleOverrideReason.trim()) {
    errors.vehicleOverrideReason = "Vehicle override reason is required for this conflict.";
  }

  const hasErrors = Object.keys(errors).length > 0;

  const showError = (key: keyof FormErrors) =>
    (touched.has(key) || submitted) ? errors[key] : undefined;

  const selectedOrderIds = useMemo(() => new Set(selectedOrders.map((o) => o.orderId)), [selectedOrders]);
  const plannedTrayTotal = selectedOrders.reduce(
    (total, order) => total + order.lines
      .filter((line) => line.unitName.toLowerCase().startsWith("tray"))
      .reduce((lineTotal, line) => lineTotal + line.plannedQuantity, 0),
    0
  );
  const plannedPieceTotal = selectedOrders.reduce(
    (total, order) => total + order.lines
      .filter((line) => line.unitName.toLowerCase().includes("piece"))
      .reduce((lineTotal, line) => lineTotal + line.plannedQuantity, 0),
    0
  );

  const selectedStopGroups = groupOrdersByStop(selectedOrders, resolvePlanningStop);

  const toSelectedOrder = (order: PlannerEligibleOrderSummaryDto): SelectedPlannerOrder => ({
    ...order,
    stopExpectedDeliveryDate: "",
    stopExpectedDeliveryTime: "",
    lines: (order.lines ?? [])
      .filter((line) => line.availableToPlan > 0)
      .map((line) => ({
        ...line,
        plannedQuantity: line.availableToPlan,
      })),
  });

  const toggleOrder = (order: PlannerEligibleOrderSummaryDto | SelectedPlannerOrder) => {
    setSelectedOrders((prev) => {
      if (prev.some((o) => o.orderId === order.orderId)) {
        return prev.filter((o) => o.orderId !== order.orderId);
      }
      return [...prev, toSelectedOrder(order as PlannerEligibleOrderSummaryDto)];
    });
    setConflictResult(null);
  };

  const updateLineQuantity = (orderId: string, fulfilmentReleaseItemId: string, value: string) => {
    setSelectedOrders((prev) =>
      prev.map((order) => {
        if (order.orderId !== orderId) return order;
        const lines = order.lines.map((line) =>
          line.fulfilmentReleaseItemId === fulfilmentReleaseItemId
            ? { ...line, plannedQuantity: clampPlanningQuantity(value, line.availableToPlan) }
            : line
        );
        const plannedTotal = lines.reduce((sum, line) => sum + line.plannedQuantity, 0);
        return {
          ...order,
          availableToPlan: plannedTotal,
          remainingAfterCurrentPlan: Math.max(0, order.quantityPending - order.alreadyPlannedQuantity),
          lines,
        };
      })
    );
    setConflictResult(null);
  };

  const updateStopGroupEta = (
    orderIds: string[],
    field: "stopExpectedDeliveryDate" | "stopExpectedDeliveryTime",
    value: string,
  ) => {
    const orderIdSet = new Set(orderIds);
    setSelectedOrders((previous) => previous.map((order) =>
      orderIdSet.has(order.orderId) ? { ...order, [field]: value } : order
    ));
    setConflictResult(null);
  };

  const moveStopGroup = (groupIndex: number, direction: -1 | 1) => {
    setSelectedOrders((prev) => {
      const groups = groupOrdersByStop(prev, resolvePlanningStop);
      const nextIndex = groupIndex + direction;
      if (nextIndex < 0 || nextIndex >= groups.length) return prev;
      [groups[groupIndex], groups[nextIndex]] = [groups[nextIndex], groups[groupIndex]];
      return groups.flatMap((group) => group.orders);
    });
    setConflictResult(null);
  };

  const suggestRoute = async () => {
    setSuggestingRoute(true);
    setRouteSuggestionMessage(null);
    try {
      const suggestion = await deliveryPlansApi.suggestRoute(selectedOrders.map((order) => ({
        orderId: order.orderId,
        latitude: order.deliveryLatitude,
        longitude: order.deliveryLongitude,
      })), selectedRouteDetail?.departureLatitude != null && selectedRouteDetail?.departureLongitude != null
        ? { latitude: selectedRouteDetail.departureLatitude, longitude: selectedRouteDetail.departureLongitude }
        : undefined);
      const position = new Map(suggestion.orderIds.map((id, index) => [id, index]));
      setSelectedOrders((orders) => [...orders].sort((a, b) =>
        (position.get(a.orderId) ?? Number.MAX_SAFE_INTEGER) - (position.get(b.orderId) ?? Number.MAX_SAFE_INTEGER)));
      setConflictResult(null);
      setRouteSuggestionMessage(suggestion.source === "Road"
        ? "Suggested stop order uses road travel times. Review and reorder as needed."
        : suggestion.source === "StraightLine"
          ? "Routing was unavailable; suggested order uses straight-line distance. Review before publishing."
          : "Add map pins to at least two destinations for a suggestion; manual stop ordering remains available.");
    } catch (error) {
      setRouteSuggestionMessage(normalizeError(error).userMessage);
    } finally {
      setSuggestingRoute(false);
    }
  };

  const buildStopInputs = (): DeliveryPlanStopInput[] => {
    const grouped = new Map<string, SelectedPlannerOrder[]>();
    selectedOrders.forEach((order) => {
      const planningStop = resolvePlanningStop(order);
      const key = planningStop?.id ?? (order.deliveryLocation.trim().toLowerCase() || order.orderId);
      grouped.set(key, [...(grouped.get(key) ?? []), order]);
    });

    const selectedOrderStops: DeliveryPlanStopInput[] = [...grouped.values()].map((ordersAtStop) => {
      const first = ordersAtStop[0];
      const planningStop = resolvePlanningStop(first);
      const etaTime = parseTimeTo24Hour(first.stopExpectedDeliveryTime);
      return {
        // Preserve the legacy header link only when the stop contains one order.
        orderId: ordersAtStop.length === 1 ? first.orderId : undefined,
        customerId: ordersAtStop.every((order) => order.customerId === first.customerId)
          ? first.customerId || undefined : undefined,
        routePointId: planningStop?.id,
        expectedDeliveryTime: first.stopExpectedDeliveryDate && etaTime
          ? new Date(combineDatetime(first.stopExpectedDeliveryDate, etaTime)).toISOString()
          : undefined,
        sequenceNumber: 0,
        items: ordersAtStop.flatMap((order) => order.lines
          .filter((line) => line.plannedQuantity > 0)
          .map((line) => ({
            orderId: order.orderId,
            orderItemId: line.orderItemId,
            plannedQuantity: line.plannedQuantity,
            fulfilmentReleaseItemId: line.fulfilmentReleaseItemId || undefined,
          }))),
      };
    });

    const merged = [...selectedOrderStops, ...preservedStops];
    return merged.map((stop, index) => ({
      ...stop,
      sequenceNumber: index + 1,
    }));
  };

  const liveCheckDepartureTime = useMemo(
    () => parseTimeTo24Hour(form.departureTime),
    [form.departureTime]
  );
  const latestStopEta = useMemo(() => buildStopInputs()
    .map((stop) => stop.expectedDeliveryTime)
    .filter((value): value is string => !!value)
    .sort()
    .at(-1), [selectedOrders, preservedStops]);

  const driverAvailabilityProbe = useMemo(() => {
    if (!form.driverId || !form.departureDate || !liveCheckDepartureTime) return null;
    const start = new Date(combineDatetime(form.departureDate, liveCheckDepartureTime));
    if (Number.isNaN(start.getTime())) return null;
    const selectedEnd = latestStopEta ? new Date(latestStopEta) : null;
    const hasValidTripEnd = selectedEnd && !Number.isNaN(selectedEnd.getTime()) && selectedEnd > start;
    const end = hasValidTripEnd ? selectedEnd : new Date(start.getTime() + 15 * 60 * 1000);
    return {
      request: {
        driverId: form.driverId,
        startDateTime: start.toISOString(),
        endDateTime: end.toISOString(),
        excludeDeliveryPlanId: detail?.id,
      },
      departureOnly: !hasValidTripEnd,
    };
  }, [detail?.id, form.departureDate, form.driverId, latestStopEta, liveCheckDepartureTime]);

  const {
    data: driverAvailability,
    isFetching: driverAvailabilityChecking,
    isError: driverAvailabilityFailed,
  } = useQuery({
    queryKey: ["delivery-plan-driver-availability", driverAvailabilityProbe?.request],
    enabled: open && !!driverAvailabilityProbe,
    retry: false,
    queryFn: () => driverAvailabilityApi.check(driverAvailabilityProbe!.request),
  });

  const availabilityProbe = useMemo<CheckDeliveryPlanConflictsRequest | null>(() => {
    if (
      !form.driverId ||
      !form.vehicleId ||
      !form.departureDate ||
      !liveCheckDepartureTime ||
      !latestStopEta
    ) {
      return null;
    }

    const plannedDeparture = combineDatetime(form.departureDate, liveCheckDepartureTime);
    if (!plannedDeparture || plannedDeparture >= latestStopEta) return null;

    return {
      deliveryPlanId: detail?.id,
      startDate: new Date(form.departureDate).toISOString(),
      endDate: latestStopEta,
      routeId: form.routeId || undefined,
      driverId: form.driverId,
      vehicleId: form.vehicleId,
      plannedDepartureTime: new Date(plannedDeparture).toISOString(),
      expectedDeliveryTime: latestStopEta,
      stops: buildStopInputs(),
      driverOverrideReason: form.driverOverrideReason.trim() || undefined,
      vehicleOverrideReason: form.vehicleOverrideReason.trim() || undefined,
    };
  }, [
    detail?.id,
    form.routeId,
    form.driverId,
    form.vehicleId,
    form.departureDate,
    form.driverOverrideReason,
    form.vehicleOverrideReason,
    liveCheckDepartureTime,
    latestStopEta,
    selectedOrders,
    preservedStops,
  ]);

  const debouncedAvailabilityProbe = useDebounce(availabilityProbe, 350);

  const {
    data: liveConflictResult,
    isFetching: liveConflictChecking,
  } = useQuery({
    queryKey: ["delivery-plan-live-availability", debouncedAvailabilityProbe],
    enabled: open && !!debouncedAvailabilityProbe,
    retry: false,
    queryFn: async () => {
      if (!debouncedAvailabilityProbe) return null;
      try {
        return await deliveryPlansApi.checkConflicts(debouncedAvailabilityProbe);
      } catch (error) {
        return conflictResultFromError(error);
      }
    },
  });

  const effectiveConflictResult = conflictResult ?? liveConflictResult ?? null;
  const hasVehicleConflictLive =
    effectiveConflictResult?.conflicts.some((c) => c.code === "VEHICLE_CONFLICT") ?? false;
  const canAssessAvailability = !!debouncedAvailabilityProbe;
  const vehicleConflictMessage =
    effectiveConflictResult?.conflicts.find((c) => c.code === "VEHICLE_CONFLICT")?.message;

  useEffect(() => {
    if (!liveConflictChecking && liveConflictResult) {
      setConflictResult(liveConflictResult);
    }
  }, [liveConflictChecking, liveConflictResult]);

  const handleSubmit = async () => {
    setSubmitted(true);
    if (hasErrors) return;

    const normalizedDepartureTime = parseTimeTo24Hour(form.departureTime);
    const plannedDepartureTime = form.departureDate && normalizedDepartureTime
      ? new Date(combineDatetime(form.departureDate, normalizedDepartureTime)).toISOString()
      : undefined;
    const expectedDeliveryTime = latestStopEta;
    const stops = buildStopInputs();

    let hasDriverConflict = false;
    let hasVehicleConflict = false;
    if (availabilityProbe) {
      let conflictCheck: DeliveryPlanConflictCheckResultDto;
      try {
        conflictCheck = await checkConflictsMutation.mutateAsync(availabilityProbe);
        setConflictResult(conflictCheck);
      } catch {
        return;
      }
      hasDriverConflict = conflictCheck.conflicts.some((c) => c.code === "DRIVER_CONFLICT");
      hasVehicleConflict = conflictCheck.conflicts.some((c) => c.code === "VEHICLE_CONFLICT");
    }

    if (hasDriverConflict) return;

    if (hasVehicleConflict && !form.vehicleOverrideReason.trim()) {
      setTouched((prev) => new Set(prev).add("vehicleOverrideReason"));
      return;
    }

    const basePayload = {
      name: form.name.trim(),
      planType: form.planType,
      startDate: new Date(form.departureDate || today).toISOString(),
      endDate: expectedDeliveryTime ?? new Date(form.departureDate || today).toISOString(),
      routeId: form.routeId || undefined,
      driverId: form.driverId || undefined,
      vehicleId: form.vehicleId || undefined,
      plannedDepartureTime,
      expectedDeliveryTime,
      driverOverrideReason: undefined,
      vehicleOverrideReason: form.vehicleOverrideReason.trim() || undefined,
      notes: form.notes.trim() || undefined,
      stops,
    };

    if (isEditMode && detail) {
      const payload: UpdateDeliveryPlanRequest = {
        ...basePayload,
        status: detail.status,
        isActive: detail.isActive,
      };
      updateMutation.mutate({ id: detail.id, data: payload }, { onSuccess: () => onClose() });
      return;
    }

    const payload: CreateDeliveryPlanRequest = basePayload;
    createMutation.mutate(payload, { onSuccess: () => onClose() });
  };

  const handleDriverChange = (driverId: string) => {
    const driver = drivers.find((candidate) => candidate.id === driverId);
    const assignedVehicleId = findAssignedVehicleId(driver?.defaultVehicleReg, vehicles);

    setForm((previous) => ({
      ...previous,
      driverId,
      vehicleId: assignedVehicleId ?? "",
    }));
    setTouched((previous) => new Set(previous).add("driverId"));
    setConflictResult(null);
  };

  const selectedVehicle = vehicles.find((v) => v.id === form.vehicleId);
  const plannedVehicleLoad = selectedVehicle ? selectedOrders.reduce<number | null>((total, order) => {
    if (total === null) return null;
    return order.lines.reduce<number | null>((lineTotal, line) => {
      if (lineTotal === null || line.plannedQuantity <= 0) return lineTotal;
      if (line.unit.toLowerCase() === selectedVehicle.capacityUnit.toLowerCase())
        return lineTotal + line.plannedQuantity;
      const trayCapacity = line.trayType === "TwoTenHole" || line.trayType === "210" ? 210
        : line.trayType === "FourFortyTwoHole" || line.trayType === "442" ? 442 : null;
      if (!trayCapacity) return null;
      if (line.unit.toLowerCase().startsWith("piece") && selectedVehicle.capacityUnit.toLowerCase().startsWith("tray"))
        return lineTotal + line.plannedQuantity / trayCapacity;
      if (line.unit.toLowerCase().startsWith("tray") && selectedVehicle.capacityUnit.toLowerCase().startsWith("piece"))
        return lineTotal + line.plannedQuantity * trayCapacity;
      return null;
    }, total);
  }, 0) : null;
  const selectedDriver = drivers.find((driver) => driver.id === form.driverId);
  const selectedDriverVehicleId = findAssignedVehicleId(selectedDriver?.defaultVehicleReg, vehicles);
  const usingDriverAssignedVehicle = !!selectedDriverVehicleId && selectedDriverVehicleId === form.vehicleId;
  const activeMutationError = updateMutation.error ?? createMutation.error;
  const activeMutationErrorMessage = activeMutationError
    ? normalizeError(activeMutationError).userMessage
    : null;
  const structuralEditRestriction = isEditMode
    ? getPlanStructuralEditRestriction(detail?.status)
    : undefined;
  const isSubmitting = createMutation.isPending || updateMutation.isPending || checkConflictsMutation.isPending;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[98vw] max-h-[96dvh] p-0 overflow-hidden sm:!max-w-[1600px]">
        <div className="flex h-full max-h-[96dvh] flex-col">
          <DialogHeader className="px-6 pt-6 pb-4 border-b">
            <DialogTitle className="text-xl">{isEditMode ? "Edit Delivery Plan" : "Create Delivery Plan"}</DialogTitle>
            <p className="text-sm text-muted-foreground">
              {isEditMode
                ? "Update route assignment, schedule, and stops for this plan."
                : "Schedule a delivery run by setting route, planned driver, and planned vehicle."}
            </p>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* ── Compact departure schedule ── */}
          <div className="grid gap-3 rounded-lg border bg-muted/30 p-3 md:grid-cols-[180px_1fr_1fr] md:items-end">
            <div className="self-center">
              <p className="flex items-center gap-2 text-sm font-medium">
                <Clock className="h-4 w-4" /> Planned departure
              </p>
              <p className="mt-1 text-xs text-muted-foreground">Optional for drafts; required before publishing.</p>
            </div>
            <Field data-invalid={!!showError("departureDate")}>
              <FieldLabel>Date</FieldLabel>
              <DatePicker
                value={form.departureDate}
                onChange={(v) => { set("departureDate", v); touch("departureDate"); }}
                min={today}
                placeholder="Departure date"
              />
              {showError("departureDate") && <FieldError>{showError("departureDate")}</FieldError>}
            </Field>
            <Field data-invalid={!!showError("departureTime")}>
              <FieldLabel>Time</FieldLabel>
              <Select
                value={form.departureTime}
                onValueChange={(v) => { set("departureTime", v); touch("departureTime"); }}
              >
                <SelectTrigger className="w-full" aria-invalid={!!showError("departureTime")}>
                  <SelectValue placeholder="Select departure time" />
                </SelectTrigger>
                <SelectContent>
                  {form.departureTime && !TIME_SLOT_OPTIONS.some((option) => option.value === form.departureTime) && (
                    <SelectItem value={form.departureTime}>{form.departureTime} (custom)</SelectItem>
                  )}
                  {TIME_SLOT_OPTIONS.map((slot) => (
                    <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {showError("departureTime") && <FieldError>{showError("departureTime")}</FieldError>}
            </Field>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_1fr] gap-6">
            {/* ── Section: Plan Details ── */}
            <FieldSet>
              <FieldLegend>Plan Details</FieldLegend>
              <FieldGroup>
                <Field data-invalid={!!showError("name")}>
                  <FieldLabel>
                    Plan name <span className="text-destructive">*</span>
                  </FieldLabel>
                  <Input
                    value={form.name}
                    onChange={(e) => set("name", e.target.value)}
                    onBlur={() => touch("name")}
                    placeholder="e.g. Thika AM Run — 28 Jul"
                    maxLength={200}
                    aria-invalid={!!showError("name")}
                  />
                  <div className="flex justify-between items-center">
                    {showError("name") ? <FieldError>{showError("name")}</FieldError> : <span />}
                    <span className="text-xs text-muted-foreground">{form.name.length}/200</span>
                  </div>
                </Field>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field>
                    <FieldLabel>Plan type</FieldLabel>
                    <Select value={form.planType} onValueChange={(v) => set("planType", v)}>
                      <SelectTrigger className="w-full"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PLAN_TYPE_OPTIONS.map((t) => (
                          <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>

                  <Field data-invalid={!!showError("routeId")}>
                    <FieldLabel>
                      Route
                    </FieldLabel>
                    <Select value={form.routeId || "none"} onValueChange={(v) => { set("routeId", v === "none" ? "" : v); touch("routeId"); }}>
                      <SelectTrigger className="w-full" aria-invalid={!!showError("routeId")}>
                        <SelectValue placeholder={routesLoading ? "Loading..." : "Select route"} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Plan from selected orders</SelectItem>
                        {routes.map((r) => (
                          <SelectItem key={r.id} value={r.id}>
                            <span className="font-medium">{r.name}</span>
                            {r.departureName && (
                              <span className="text-muted-foreground ml-1.5 text-xs">
                                from {r.departureName}
                              </span>
                            )}
                          </SelectItem>
                        ))}
                        {routes.length === 0 && !routesLoading && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No active routes</div>
                        )}
                      </SelectContent>
                    </Select>
                    {showError("routeId") && <FieldError>{showError("routeId")}</FieldError>}
                    {selectedRoute && (
                      <FieldDescription>
                        {selectedRoute.stopCount} stop{selectedRoute.stopCount !== 1 ? "s" : ""}
                        {selectedRoute.estimatedDistanceKm ? ` · ${selectedRoute.estimatedDistanceKm} km` : ""}
                        {selectedRoute.estimatedDurationMinutes ? ` · ~${selectedRoute.estimatedDurationMinutes} min` : ""}
                      </FieldDescription>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </FieldSet>

            {/* ── Section: Assignment ── */}
            <FieldSet>
              <FieldLegend>
                <span className="inline-flex items-center gap-2">
                  <User className="h-3.5 w-3.5" /> Assignment
                </span>
              </FieldLegend>
              <FieldGroup>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-1 gap-4">
                  <Field data-invalid={!!showError("driverId")}>
                    <FieldLabel>
                      Planned Driver
                    </FieldLabel>
                    <Select value={form.driverId} onValueChange={handleDriverChange} disabled={driversLoading || vehiclesLoading}>
                      <SelectTrigger className="w-full" aria-invalid={!!showError("driverId")}>
                        <SelectValue placeholder={driversLoading ? "Loading..." : "Select driver"} />
                      </SelectTrigger>
                      <SelectContent>
                        {drivers.map((d) => (
                          <SelectItem key={d.id} value={d.id}>
                            {d.fullName}
                            {d.defaultVehicleReg && (
                              <span className="text-muted-foreground ml-1.5 text-xs">({d.defaultVehicleReg})</span>
                            )}
                          </SelectItem>
                        ))}
                        {drivers.length === 0 && !driversLoading && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No active drivers</div>
                        )}
                      </SelectContent>
                    </Select>
                    {showError("driverId") && <FieldError>{showError("driverId")}</FieldError>}
                    {driverAvailabilityProbe && (
                      <div className="mt-1.5">
                        {driverAvailabilityChecking ? (
                          <Badge variant="secondary">Checking availability...</Badge>
                        ) : driverAvailabilityFailed ? (
                          <Badge variant="secondary">Availability check unavailable</Badge>
                        ) : driverAvailability?.isAvailable ? (
                          <Badge variant="outline" className="text-green-700 border-green-300">
                            {driverAvailabilityProbe.departureOnly ? "Available at departure" : "Available for planned trip"}
                          </Badge>
                        ) : (
                          <Badge variant="destructive">Driver unavailable for selected window</Badge>
                        )}
                      </div>
                    )}
                    {!driverAvailabilityChecking && driverAvailability && !driverAvailability.isAvailable && (
                      <FieldDescription>
                        {driverAvailability.conflicts[0]?.message ?? "No availability window covers the selected time."}
                      </FieldDescription>
                    )}
                    {driverAvailability?.isAvailable && driverAvailabilityProbe?.departureOnly && (
                      <FieldDescription>Add an optional stop ETA to check availability for the full trip.</FieldDescription>
                    )}
                    {form.driverId && !driverAvailabilityProbe && (
                      <FieldDescription>Select a planned departure to preview this driver's availability.</FieldDescription>
                    )}
                    <FieldDescription>
                      This driver will be inherited by executable dispatch trips when published.
                    </FieldDescription>
                  </Field>

                  <Field data-invalid={!!showError("vehicleId")}>
                    <FieldLabel>
                      Planned Vehicle
                    </FieldLabel>
                    <Select value={form.vehicleId} onValueChange={(v) => { set("vehicleId", v); touch("vehicleId"); }} disabled={vehiclesLoading}>
                      <SelectTrigger className="w-full" aria-invalid={!!showError("vehicleId")}>
                        <SelectValue placeholder={vehiclesLoading ? "Loading..." : "Select vehicle"} />
                      </SelectTrigger>
                      <SelectContent>
                        {vehicles.map((v) => (
                          <SelectItem key={v.id} value={v.id} disabled={!isVehicleAvailableForPlanning(v.status)}>
                            <span className="font-medium">{v.registrationNumber}</span>
                            {v.nameOrDescription && (
                              <span className="text-muted-foreground ml-1.5 text-xs">{v.nameOrDescription}</span>
                            )}
                            {v.status !== "Available" && (
                              <span className="text-muted-foreground ml-1.5 text-xs">· {v.status}</span>
                            )}
                          </SelectItem>
                        ))}
                        {vehicles.length === 0 && !vehiclesLoading && (
                          <div className="px-3 py-2 text-sm text-muted-foreground">No active vehicles</div>
                        )}
                      </SelectContent>
                    </Select>
                    {showError("vehicleId") && <FieldError>{showError("vehicleId")}</FieldError>}
                    {canAssessAvailability && (
                      <div className="mt-1.5">
                        {liveConflictChecking ? (
                          <Badge variant="secondary">Checking availability...</Badge>
                        ) : hasVehicleConflictLive ? (
                          <Badge variant="destructive">Vehicle unavailable for selected window</Badge>
                        ) : (
                          <Badge variant="outline" className="text-green-700 border-green-300">Vehicle available</Badge>
                        )}
                      </div>
                    )}
                    {!liveConflictChecking && vehicleConflictMessage && (
                      <FieldDescription>{vehicleConflictMessage}</FieldDescription>
                    )}
                    {selectedVehicle && (
                      <FieldDescription>
                        <Truck className="inline h-3 w-3 mr-1" />
                        {selectedVehicle.vehicleType ?? "Vehicle"}
                        {` · Capacity: ${selectedVehicle.capacity} ${selectedVehicle.capacityUnit}`}
                        {plannedVehicleLoad === null
                          ? " · Load: incompatible units or unknown tray type"
                          : ` · Planned load: ${formatQuantity(plannedVehicleLoad)} ${selectedVehicle.capacityUnit}${plannedVehicleLoad > selectedVehicle.capacity ? " (over capacity)" : ""}`}
                      </FieldDescription>
                    )}
                    <FieldDescription>
                      {usingDriverAssignedVehicle
                        ? `Defaulted to ${selectedDriver?.fullName}'s assigned vehicle. You can select another available vehicle.`
                        : selectedDriver?.defaultVehicleReg && !selectedDriverVehicleId
                          ? `Assigned vehicle ${selectedDriver.defaultVehicleReg} is unavailable, inactive, or could not be found. Select another available vehicle.`
                          : form.driverId
                            ? "Select any available vehicle for this driver. You can change it without changing the driver's default assignment."
                            : "The selected vehicle will be inherited by executable dispatch trips when published."}
                    </FieldDescription>
                  </Field>
                </div>
              </FieldGroup>
            </FieldSet>
          </div>

          <Separator />

          {/* ── Section: Stops ── */}
          <FieldSet>
            <FieldLegend>Plan delivery stops</FieldLegend>
            <FieldDescription>Find order → Add → Set quantity → Review under Selected Stops.</FieldDescription>

            <div className="mt-3 grid gap-4 xl:h-[580px] xl:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
              {/* Eligible orders */}
              <section className="flex min-h-[420px] flex-col overflow-hidden rounded-lg border" aria-label="Eligible orders">
                <div className="border-b bg-background p-3">
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-sm font-semibold">Eligible Orders</h3>
                    <span className="text-xs text-muted-foreground">{eligibleTotalCount} matching</span>
                  </div>
                  <div className="relative mt-2">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={eligibleSearch}
                      onChange={(event) => {
                        setEligibleSearch(event.target.value);
                        setEligiblePage(1);
                      }}
                      placeholder="Search order, customer, or location"
                      className="pl-9"
                      aria-label="Search eligible orders"
                    />
                  </div>
                  <div className="mt-2 flex gap-2 overflow-x-auto pb-1" aria-label="Filter eligible orders by pricing region and area">
                    <Button
                      type="button"
                      size="sm"
                      variant={selectedLocation === "all" ? "default" : "outline"}
                      onClick={() => { setSelectedLocation("all"); setEligiblePage(1); }}
                      className="shrink-0"
                    >
                      All {allLocationOrderCount}
                    </Button>
                    {eligibleLocationCounts.map((location) => (
                      <Button
                        key={location.location}
                        type="button"
                        size="sm"
                        variant={selectedLocation === location.location ? "default" : "outline"}
                        onClick={() => { setSelectedLocation(location.location); setEligiblePage(1); }}
                        className="shrink-0"
                      >
                        {location.location} {location.orderCount}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto divide-y">
                  {eligibleOrdersLoading || (eligibleOrdersFetching && !eligibleOrderResult) ? (
                    <p className="p-6 text-center text-sm text-muted-foreground">Loading eligible orders…</p>
                  ) : eligibleOrders.length === 0 ? (
                    <div className="p-6 text-center text-sm text-muted-foreground">
                      {eligibleSearch ? (
                        <>
                          <p>No eligible orders match “{eligibleSearch}”.</p>
                          <Button type="button" variant="link" size="sm" onClick={() => { setEligibleSearch(""); setEligiblePage(1); }}>Clear search</Button>
                        </>
                      ) : selectedLocation !== "all" ? (
                        <>
                          <p>No eligible orders for {selectedLocation}.</p>
                          <Button type="button" variant="link" size="sm" onClick={() => { setSelectedLocation("all"); setEligiblePage(1); }}>Show all locations</Button>
                        </>
                      ) : (
                        <p>No eligible Sales-released delivery orders are available.</p>
                      )}
                    </div>
                  ) : (
                    eligibleOrders.map((order) => {
                      const selected = selectedOrderIds.has(order.orderId);
                      const expanded = expandedEligibleOrderIds.has(order.orderId);
                      const hasPlanningStop = !!resolvePlanningStop(order);
                      const addRestriction = getOrderAddRestriction({
                        selected,
                        availableToPlan: order.availableToPlan,
                        requiresRouteStop: order.requiresRouteStop,
                        selectedRouteId: form.routeId || undefined,
                        hasPlanningStop,
                        planStatus: isEditMode ? detail?.status : undefined,
                      });
                      return (
                        <div key={order.orderId} className={selected ? "bg-primary/5" : undefined}>
                          <div className="flex items-center gap-3 p-3">
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-semibold">{order.orderNumber}</p>
                              <p className="truncate text-xs text-muted-foreground">
                                {order.customerName} · {order.pricingRegion && order.deliveryAreaName
                                  ? `${order.pricingRegion} · ${order.deliveryAreaName} · ` : ""}{order.deliveryLocation}
                              </p>
                              {!selected && addRestriction && (
                                <p className="mt-1 text-xs text-amber-700">{addRestriction}</p>
                              )}
                            </div>
                            <p className="shrink-0 text-right text-sm font-medium">
                              {formatQuantityBreakdown(order.lines ?? [], (line) => line.availableToPlan)}
                              <span className="block text-xs font-normal text-muted-foreground">available</span>
                            </p>
                            <Button
                              type="button"
                              size="sm"
                              variant={selected ? "secondary" : "outline"}
                              onClick={() => toggleOrder(order)}
                              disabled={!!addRestriction}
                              title={addRestriction}
                            >
                              {selected ? "Added" : "Add"}
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 shrink-0"
                              aria-label={`${expanded ? "Hide" : "Show"} details for ${order.orderNumber}`}
                              aria-expanded={expanded}
                              onClick={() => setExpandedEligibleOrderIds((previous) => {
                                const next = new Set(previous);
                                if (next.has(order.orderId)) next.delete(order.orderId);
                                else next.add(order.orderId);
                                return next;
                              })}
                            >
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </Button>
                          </div>
                          {expanded && (
                            <div className="space-y-2 border-t bg-muted/20 px-3 py-2">
                              {(order.lines ?? []).map((line) => (
                                <div key={line.fulfilmentReleaseItemId} className="rounded-md border bg-background p-2 text-xs">
                                  <div className="flex min-w-0 items-center gap-2">
                                    <p className="truncate font-medium">{line.productName}{line.variety ? ` · ${line.variety}` : ""}</p>
                                    <Badge variant="outline" className="shrink-0 font-normal">{formatOrderLinePackaging(line)}</Badge>
                                  </div>
                                  <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-muted-foreground sm:grid-cols-3">
                                    <span>Ready: {formatQuantity(line.productionReadyQuantity)}</span>
                                    <span>Sales released: {formatQuantity(line.salesReleasedQuantity)}</span>
                                    <span>Delivered: {formatQuantity(line.deliveredQuantity)}</span>
                                    <span>Already planned: {formatQuantity(line.alreadyPlannedQuantity)}</span>
                                    <span className="font-medium text-foreground">Available: {formatQuantity(line.availableToPlan)}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="flex items-center justify-between gap-2 border-t bg-background px-3 py-2 text-xs text-muted-foreground">
                  <span>
                    {eligibleTotalCount === 0
                      ? "No results"
                      : `${(eligiblePage - 1) * 20 + 1}–${Math.min(eligiblePage * 20, eligibleTotalCount)} of ${eligibleTotalCount}`}
                  </span>
                  <div className="flex items-center gap-1">
                    <Button type="button" size="sm" variant="outline" onClick={() => setEligiblePage((page) => page - 1)} disabled={eligiblePage <= 1 || eligibleOrdersFetching}>Previous</Button>
                    <span className="px-2">Page {eligiblePage} of {Math.max(1, eligibleTotalPages)}</span>
                    <Button type="button" size="sm" variant="outline" onClick={() => setEligiblePage((page) => page + 1)} disabled={eligiblePage >= eligibleTotalPages || eligibleOrdersFetching}>Next</Button>
                  </div>
                </div>
              </section>

              {/* Selected stop summary */}
              <section className="flex min-h-[420px] flex-col overflow-hidden rounded-lg border" aria-label="Selected stops">
                <div className="border-b bg-muted/30 px-3 py-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <h3 className="text-sm font-semibold">Selected Stops</h3>
                    <Button type="button" size="sm" variant="outline" onClick={suggestRoute}
                      disabled={suggestingRoute || selectedOrders.length < 2 || selectedOrders.length > 10}>
                      {suggestingRoute ? "Suggesting…" : "Suggest route"}
                    </Button>
                    <p className="text-sm font-medium">
                      {selectedStopGroups.length} stop{selectedStopGroups.length === 1 ? "" : "s"} · {selectedOrders.length} order{selectedOrders.length === 1 ? "" : "s"} · {formatQuantity(plannedTrayTotal)} tray{plannedTrayTotal === 1 ? "" : "s"}
                      {plannedPieceTotal > 0 ? ` · ${formatQuantity(plannedPieceTotal)} pieces` : ""}
                    </p>
                  </div>
                  {routeSuggestionMessage && <p className="mt-1 text-xs text-muted-foreground">{routeSuggestionMessage}</p>}
                </div>

                <div className="min-h-0 flex-1 overflow-y-auto p-3">
                  {selectedStopGroups.length === 0 ? (
                    <div className="flex h-full min-h-48 items-center justify-center text-center">
                      <div>
                        <p className="text-sm font-medium">No selected stops yet</p>
                        <p className="mt-1 text-xs text-muted-foreground">Add an eligible order, then set the quantity here.</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {selectedStopGroups.map((group, groupIndex) => {
                        const orderIds = group.orders.map((order) => order.orderId);
                        const firstOrder = group.orders[0];
                        const groupLines = group.orders.flatMap((order) => order.lines);
                        const expectedDeliveries = group.orders.map((order) => order.expectedDeliveryDate);
                        const earliestExpectedDelivery = expectedDeliveries
                          .filter((value): value is string => !!value)
                          .sort()[0];
                        const orderExpectationBeforeDeparture = !!earliestExpectedDelivery
                          && !!plannedDepartureForValidation
                          && new Date(earliestExpectedDelivery) <= new Date(plannedDepartureForValidation);
                        const groupEtaRestriction = getStopEtaRestriction({
                          date: firstOrder.stopExpectedDeliveryDate,
                          time: firstOrder.stopExpectedDeliveryTime,
                          plannedDeparture: plannedDepartureForValidation,
                          orderExpectedDeliveries: expectedDeliveries,
                        });
                        return (
                          <div key={group.key} className="overflow-hidden rounded-lg border">
                            <div className="bg-muted/40 p-3">
                              <div className="flex items-center gap-2">
                                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">{groupIndex + 1}</span>
                                <div className="min-w-0 flex-1">
                                  <p className="truncate text-sm font-semibold uppercase">{group.name}</p>
                                  <p className="text-xs text-muted-foreground">
                                    {group.orders.length} order{group.orders.length === 1 ? "" : "s"} · {formatQuantityBreakdown(groupLines, (line) => line.plannedQuantity)}
                                  </p>
                                </div>
                                <Button type="button" size="sm" variant="ghost" onClick={() => moveStopGroup(groupIndex, -1)} disabled={groupIndex === 0}>Up</Button>
                                <Button type="button" size="sm" variant="ghost" onClick={() => moveStopGroup(groupIndex, 1)} disabled={groupIndex === selectedStopGroups.length - 1}>Down</Button>
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="ghost"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => {
                                    const ids = new Set(orderIds);
                                    setSelectedOrders((previous) => previous.filter((order) => !ids.has(order.orderId)));
                                    setConflictResult(null);
                                  }}
                                >
                                  Remove stop
                                </Button>
                              </div>
                              <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
                                <Field data-invalid={!!groupEtaRestriction}>
                                  <FieldLabel>Stop ETA date <span className="font-normal text-muted-foreground">(optional)</span></FieldLabel>
                                  <DatePicker
                                    value={firstOrder.stopExpectedDeliveryDate}
                                    onChange={(value) => updateStopGroupEta(orderIds, "stopExpectedDeliveryDate", value)}
                                    min={form.departureDate || today}
                                    placeholder="No planned ETA"
                                  />
                                </Field>
                                <Field data-invalid={!!groupEtaRestriction}>
                                  <FieldLabel>Stop ETA time <span className="font-normal text-muted-foreground">(optional)</span></FieldLabel>
                                  <Select
                                    value={firstOrder.stopExpectedDeliveryTime}
                                    onValueChange={(value) => updateStopGroupEta(orderIds, "stopExpectedDeliveryTime", value)}
                                  >
                                    <SelectTrigger className="w-full" aria-invalid={!!groupEtaRestriction}><SelectValue placeholder="No planned ETA" /></SelectTrigger>
                                    <SelectContent>
                                      {TIME_SLOT_OPTIONS.map((slot) => (
                                        <SelectItem
                                          key={slot.value}
                                          value={slot.value}
                                          disabled={!!firstOrder.stopExpectedDeliveryDate && !!getStopEtaRestriction({
                                            date: firstOrder.stopExpectedDeliveryDate,
                                            time: slot.value,
                                            plannedDeparture: plannedDepartureForValidation,
                                            orderExpectedDeliveries: expectedDeliveries,
                                          })}
                                        >
                                          {slot.label}
                                        </SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                </Field>
                              </div>
                              <div className="mt-1 flex flex-wrap items-center justify-between gap-2 text-xs">
                                <p className={groupEtaRestriction
                                  ? "text-destructive"
                                  : orderExpectationBeforeDeparture ? "text-amber-700" : "text-muted-foreground"}>
                                  {groupEtaRestriction
                                    ?? (orderExpectationBeforeDeparture
                                      ? `Order was expected ${formatDate(earliestExpectedDelivery)} at ${formatTime(earliestExpectedDelivery)} and is overdue. Set a realistic ETA after departure when known.`
                                      : earliestExpectedDelivery
                                      ? `Order expected by ${formatDate(earliestExpectedDelivery)} at ${formatTime(earliestExpectedDelivery)}. ETA is optional until publishing.`
                                      : "ETA is optional until publishing.")}
                                </p>
                                {(firstOrder.stopExpectedDeliveryDate || firstOrder.stopExpectedDeliveryTime) && (
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="ghost"
                                    className="h-7 px-2"
                                    onClick={() => {
                                      updateStopGroupEta(orderIds, "stopExpectedDeliveryDate", "");
                                      updateStopGroupEta(orderIds, "stopExpectedDeliveryTime", "");
                                    }}
                                  >
                                    Clear ETA
                                  </Button>
                                )}
                              </div>
                            </div>

                            <div className="divide-y">
                              {group.orders.map((order) => (
                                <div key={order.orderId} className="p-3">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="min-w-0">
                                      <p className="truncate text-sm font-medium">{order.orderNumber} · {order.customerName}</p>
                                      <p className="truncate text-xs text-muted-foreground">Destination: {order.deliveryLocation}</p>
                                      <p className="text-xs text-muted-foreground">{formatQuantityBreakdown(order.lines, (line) => line.plannedQuantity)} selected</p>
                                    </div>
                                    <Button type="button" size="sm" variant="ghost" className="text-destructive hover:text-destructive" onClick={() => toggleOrder(order)}>Remove</Button>
                                  </div>
                                  <div className="mt-2 space-y-2">
                                    {order.lines.map((line) => (
                                      <div key={line.fulfilmentReleaseItemId} className="grid grid-cols-[minmax(0,1fr)_96px] items-center gap-3 rounded-md border px-2 py-2 text-xs">
                                        <div className="min-w-0">
                                          <div className="flex min-w-0 items-center gap-2">
                                            <p className="truncate font-medium">{line.productName}{line.variety ? ` · ${line.variety}` : ""}</p>
                                            <Badge variant="outline" className="shrink-0 font-normal">{formatOrderLinePackaging(line)}</Badge>
                                          </div>
                                          <p className="truncate text-muted-foreground">
                                            Released {formatQuantity(line.salesReleasedQuantity)} · Ready {formatQuantity(line.productionReadyQuantity)} · Available {formatQuantity(line.availableToPlan)}
                                          </p>
                                        </div>
                                        <div>
                                          <Input
                                            aria-label={`Planned ${line.unitName} for ${line.productName}`}
                                            type="number"
                                            min={0}
                                            max={line.availableToPlan}
                                            step="0.01"
                                            value={line.plannedQuantity}
                                            onChange={(event) => updateLineQuantity(order.orderId, line.fulfilmentReleaseItemId, event.target.value)}
                                            className="h-8"
                                          />
                                          <p className="mt-1 text-center text-muted-foreground">{line.unitName}</p>
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </section>
            </div>

            {showError("stops") && <FieldError>{showError("stops")}</FieldError>}
          </FieldSet>

          {(conflictResult?.hasConflicts || form.vehicleOverrideReason) && (
            <>
              <Separator />
              <FieldSet>
                <FieldLegend>Conflict Overrides</FieldLegend>
                <FieldGroup>
                  {conflictResult?.hasConflicts && (
                    <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                      <p className="text-sm font-medium text-amber-800">Assignment conflicts detected</p>
                      <ul className="mt-1 text-sm text-amber-700 list-disc ml-5">
                        {conflictResult.conflicts.map((conflict) => (
                          <li key={`${conflict.code}-${conflict.field ?? "field"}`}>{conflict.message}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  <div className="grid grid-cols-1 gap-4">
                    <Field data-invalid={!!showError("vehicleOverrideReason")}>
                      <FieldLabel>Vehicle override reason</FieldLabel>
                      <Textarea
                        value={form.vehicleOverrideReason}
                        onChange={(e) => set("vehicleOverrideReason", e.target.value)}
                        onBlur={() => touch("vehicleOverrideReason")}
                        rows={2}
                        placeholder="Explain why vehicle conflict should be overridden"
                        aria-invalid={!!showError("vehicleOverrideReason")}
                      />
                      <FieldDescription>
                        Required only if a vehicle overlap conflict is reported.
                      </FieldDescription>
                      {showError("vehicleOverrideReason") && <FieldError>{showError("vehicleOverrideReason")}</FieldError>}
                    </Field>
                  </div>
                </FieldGroup>
              </FieldSet>
            </>
          )}

          <Separator />

          {/* ── Notes ── */}
          <Collapsible open={notesOpen} onOpenChange={setNotesOpen}>
            <CollapsibleTrigger asChild>
              <Button type="button" variant="ghost" className="px-0 text-sm font-medium">
                {notesOpen ? <ChevronDown className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {form.notes.trim() ? "Delivery instructions" : "Add delivery instructions"}
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <Field className="mt-2">
                <Textarea
                  value={form.notes}
                  onChange={(e) => set("notes", e.target.value)}
                  rows={2}
                  placeholder="Any special instructions for this delivery run..."
                />
                <FieldDescription>Optional instructions for the driver or dispatch team.</FieldDescription>
              </Field>
            </CollapsibleContent>
          </Collapsible>

          {/* Server error */}
          {(createMutation.isError || updateMutation.isError) && (
            <div role="alert" className="rounded-md bg-destructive/10 border border-destructive/30 px-3 py-2">
              <p className="text-sm text-destructive">
                {activeMutationErrorMessage ?? (isEditMode ? "Failed to update delivery plan." : "Failed to create delivery plan.")}
              </p>
            </div>
          )}

          {structuralEditRestriction && (
            <div role="alert" className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
              <p className="text-sm text-amber-800">{structuralEditRestriction} Structural changes cannot be saved.</p>
            </div>
          )}

          {/* Validation summary on submit attempt */}
          {submitted && hasErrors && (
            <div role="alert" className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2">
              <p className="text-sm text-amber-800 font-medium">Please fix the errors above before submitting.</p>
            </div>
          )}
          </div>

          <DialogFooter className="px-6 py-4 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/85 gap-2 sm:gap-0">
            <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting || !!structuralEditRestriction}>
              {checkConflictsMutation.isPending
                ? "Checking conflicts..."
                : updateMutation.isPending
                  ? "Saving..."
                  : createMutation.isPending
                    ? "Creating..."
                    : isEditMode
                      ? "Save Changes"
                      : "Create Plan"}
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DeliveryPlanDestinationsDialog({
  plan,
  open,
  onClose,
}: {
  plan: DeliveryPlanSummaryDto;
  open: boolean;
  onClose: () => void;
}) {
  const { data: detail, isLoading } = useDeliveryPlanDetail(plan.id);
  const orders = useMemo(() => {
    if (!detail) return [];
    const byOrder = new Map<string, {
      orderId: string;
      orderNumber: string;
      customerName: string;
      deliveryLocation: string;
      deliveryInstructions?: string | null;
      expectedDeliveryTime?: string;
      lines: Array<{ key: string; label: string; quantity: number; unit: string }>;
    }>();

    detail.stops
      .slice()
      .sort((a, b) => a.sequenceNumber - b.sequenceNumber)
      .forEach((stop) => stop.items.forEach((item) => {
        const current = byOrder.get(item.orderId) ?? {
          orderId: item.orderId,
          orderNumber: item.orderNumber || stop.orderNumber || "Order",
          customerName: item.customerName || stop.customerName || "Customer",
          deliveryLocation: item.deliveryLocation || stop.routePointName || "Destination unavailable",
          deliveryInstructions: item.deliveryInstructions,
          expectedDeliveryTime: stop.expectedDeliveryTime,
          lines: [],
        };
        current.lines.push({
          key: item.id,
          label: `${item.productName}${item.variety ? ` · ${item.variety}` : ""}`,
          quantity: item.executableQuantity,
          unit: item.unitName,
        });
        byOrder.set(item.orderId, current);
      }));

    return [...byOrder.values()];
  }, [detail]);

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => { if (!nextOpen) onClose(); }}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{plan.name}</DialogTitle>
          <DialogDescription>
            {plan.routeName} · {plan.driverName} · {plan.vehicleRegistrationNumber}
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <LoadingAnimation />
        ) : orders.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">This plan has no order destinations.</p>
        ) : (
          <div className="space-y-3">
            {orders.map((order, index) => (
              <div key={order.orderId} className="rounded-lg border p-3">
                <div className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                    {index + 1}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">{order.orderNumber} — {order.customerName}</p>
                    <p className="text-sm"><span className="font-medium">Destination:</span> {order.deliveryLocation}</p>
                    {order.deliveryInstructions && (
                      <p className="text-xs text-muted-foreground">Instructions: {order.deliveryInstructions}</p>
                    )}
                    <p className="mt-1 text-xs text-muted-foreground">
                      ETA: {formatDate(order.expectedDeliveryTime)} {formatTime(order.expectedDeliveryTime)}
                    </p>
                  </div>
                </div>
                <div className="mt-2 space-y-1 pl-9 text-xs text-muted-foreground">
                  {order.lines.map((line) => (
                    <p key={line.key}>{line.label}: {formatQuantity(line.quantity)} {line.unit}</p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ReassignDeliveryPlanDialog({
  plan,
  open,
  onClose,
}: {
  plan: DeliveryPlanSummaryDto;
  open: boolean;
  onClose: () => void;
}) {
  const reassignMutation = useReassignDeliveryPlan();
  const checkConflictsMutation = useCheckDeliveryPlanConflicts();
  const { data: detail, isLoading: detailLoading } = useDeliveryPlanDetail(plan.id);
  const { data: drivers = [], isLoading: driversLoading } = useDrivers();
  const { data: vehicles = [], isLoading: vehiclesLoading } = useVehicles();

  const [form, setForm] = useState<ReassignFormState>({
    driverId: "",
    vehicleId: "",
    departureDate: isoToDateInput(plan.plannedDepartureTime),
    departureTime: isoToTimeInput(plan.plannedDepartureTime),
    reason: "",
    driverOverrideReason: "",
    vehicleOverrideReason: "",
    notes: "",
  });

  const [submitted, setSubmitted] = useState(false);
  const [conflictResult, setConflictResult] = useState<DeliveryPlanConflictCheckResultDto | null>(null);

  useEffect(() => {
    if (!detail) return;
    setForm((prev) => ({
      ...prev,
      driverId: detail.driverId ?? "",
      vehicleId: detail.vehicleId ?? "",
      departureDate: isoToDateInput(detail.plannedDepartureTime),
      departureTime: isoToTimeInput(detail.plannedDepartureTime),
      notes: "",
    }));
  }, [detail]);

  const set = (key: keyof ReassignFormState, value: string) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (
      key === "driverId" ||
      key === "vehicleId" ||
      key === "departureDate" ||
      key === "departureTime" ||
      key === "reason" ||
      key === "driverOverrideReason" ||
      key === "vehicleOverrideReason"
    ) {
      setConflictResult(null);
    }
  };

  const errors = validateReassignForm(form);
  const requiresVehicleOverride = conflictResult?.conflicts.some((c) => c.code === "VEHICLE_CONFLICT") ?? false;

  if (requiresVehicleOverride && !form.vehicleOverrideReason.trim()) {
    errors.vehicleOverrideReason = "Vehicle override reason is required for this conflict.";
  }

  const hasErrors = Object.keys(errors).length > 0;
  const showError = (key: keyof ReassignFormErrors) => (submitted ? errors[key] : undefined);

  const buildStopsForConflictCheck = (): DeliveryPlanStopInput[] =>
    (detail?.stops ?? []).map((stop) => ({
      orderId: stop.orderId,
      customerId: stop.customerId,
      routePointId: stop.routePointId,
      collectionPointId: stop.collectionPointId,
      sequenceNumber: stop.sequenceNumber,
      plannedArrivalTime: stop.plannedArrivalTime,
      expectedDeliveryTime: stop.expectedDeliveryTime,
      notes: stop.notes,
      items: (stop.items ?? []).map((item) => ({
        orderId: item.orderId,
        orderItemId: item.orderItemId,
        plannedQuantity: item.plannedQuantity,
      })),
    }));

  const runConflictCheck = async () => {
    if (!detail || !detail.routeId) return null;

    const normalizedDepartureTime = parseTimeTo24Hour(form.departureTime);
    const expectedDeliveryTime = detail.stops
      .map((stop) => stop.expectedDeliveryTime)
      .filter((value): value is string => !!value)
      .sort()
      .at(-1);
    if (!normalizedDepartureTime || !expectedDeliveryTime) return null;

    const request: CheckDeliveryPlanConflictsRequest = {
      deliveryPlanId: detail.id,
      startDate: detail.startDate,
      endDate: detail.endDate,
      routeId: detail.routeId,
      driverId: form.driverId,
      vehicleId: form.vehicleId,
      plannedDepartureTime: new Date(combineDatetime(form.departureDate, normalizedDepartureTime)).toISOString(),
      expectedDeliveryTime,
      stops: buildStopsForConflictCheck(),
      driverOverrideReason: form.driverOverrideReason.trim() || undefined,
      vehicleOverrideReason: form.vehicleOverrideReason.trim() || undefined,
    };

    try {
      const result = await checkConflictsMutation.mutateAsync(request);
      setConflictResult(result);
      return result;
    } catch {
      return null;
    }
  };

  const handleSubmit = async () => {
    setSubmitted(true);
    if (hasErrors || !detail) return;

    const conflictCheck = await runConflictCheck();
    if (!conflictCheck) return;

    const hasDriverConflict = conflictCheck.conflicts.some((c) => c.code === "DRIVER_CONFLICT");
    const hasVehicleConflict = conflictCheck.conflicts.some((c) => c.code === "VEHICLE_CONFLICT");
    if (hasDriverConflict) return;
    if (hasVehicleConflict && !form.vehicleOverrideReason.trim()) return;

    const payload: ReassignDeliveryPlanRequest = {
      driverId: form.driverId,
      vehicleId: form.vehicleId,
      plannedDepartureTime: new Date(combineDatetime(form.departureDate, parseTimeTo24Hour(form.departureTime)!)).toISOString(),
      expectedDeliveryTime: detail.stops
        .map((stop) => stop.expectedDeliveryTime)
        .filter((value): value is string => !!value)
        .sort()
        .at(-1)!,
      reason: form.reason.trim(),
      driverOverrideReason: undefined,
      vehicleOverrideReason: form.vehicleOverrideReason.trim() || undefined,
      notes: form.notes.trim() || undefined,
    };

    reassignMutation.mutate(
      { id: plan.id, data: payload },
      { onSuccess: () => onClose() }
    );
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="w-[95vw] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Reassign Delivery Plan</DialogTitle>
          <p className="text-sm text-muted-foreground">
            Reassign driver or vehicle for {plan.name} and resolve any assignment conflicts.
          </p>
        </DialogHeader>

        {detailLoading ? (
          <LoadingAnimation />
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field data-invalid={!!showError("driverId")}>
                <FieldLabel>Driver <span className="text-destructive">*</span></FieldLabel>
                <Select value={form.driverId} onValueChange={(v) => set("driverId", v)}>
                  <SelectTrigger className="w-full" aria-invalid={!!showError("driverId")}>
                    <SelectValue placeholder={driversLoading ? "Loading..." : "Select driver"} />
                  </SelectTrigger>
                  <SelectContent>
                    {drivers.map((driver) => (
                      <SelectItem key={driver.id} value={driver.id}>{driver.fullName}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showError("driverId") && <FieldError>{showError("driverId")}</FieldError>}
              </Field>

              <Field data-invalid={!!showError("vehicleId")}>
                <FieldLabel>Vehicle <span className="text-destructive">*</span></FieldLabel>
                <Select value={form.vehicleId} onValueChange={(v) => set("vehicleId", v)}>
                  <SelectTrigger className="w-full" aria-invalid={!!showError("vehicleId")}>
                    <SelectValue placeholder={vehiclesLoading ? "Loading..." : "Select vehicle"} />
                  </SelectTrigger>
                  <SelectContent>
                    {vehicles.map((vehicle) => (
                      <SelectItem key={vehicle.id} value={vehicle.id}>{vehicle.registrationNumber}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {showError("vehicleId") && <FieldError>{showError("vehicleId")}</FieldError>}
              </Field>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="font-medium">Departure</div>
                <div className="text-muted-foreground">
                  {formatDate(detail?.plannedDepartureTime)} {formatTime(detail?.plannedDepartureTime)}
                </div>
              </div>
              <div>
                <div className="font-medium">Final stop ETA</div>
                <div className="text-muted-foreground">
                  {formatDate(detail?.expectedDeliveryTime)} {formatTime(detail?.expectedDeliveryTime)}
                </div>
              </div>
            </div>

            <Field data-invalid={!!showError("reason")}>
              <FieldLabel>Reason <span className="text-destructive">*</span></FieldLabel>
              <Textarea
                value={form.reason}
                onChange={(e) => set("reason", e.target.value)}
                rows={3}
                placeholder="Vehicle unavailable, driver unavailable, or other operational reason"
                aria-invalid={!!showError("reason")}
              />
              {showError("reason") && <FieldError>{showError("reason")}</FieldError>}
            </Field>

            {(conflictResult?.hasConflicts || form.vehicleOverrideReason) && (
              <>
                {conflictResult?.hasConflicts && (
                  <div className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-sm font-medium text-amber-800">Assignment conflicts detected</p>
                    <ul className="mt-1 text-sm text-amber-700 list-disc ml-5">
                      {conflictResult.conflicts.map((conflict) => (
                        <li key={`${conflict.code}-${conflict.field ?? "field"}`}>{conflict.message}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="grid grid-cols-1 gap-4">
                  <Field data-invalid={!!showError("vehicleOverrideReason")}>
                    <FieldLabel>Vehicle override reason</FieldLabel>
                    <Textarea
                      value={form.vehicleOverrideReason}
                      onChange={(e) => set("vehicleOverrideReason", e.target.value)}
                      rows={2}
                      placeholder="Required when vehicle conflict exists"
                      aria-invalid={!!showError("vehicleOverrideReason")}
                    />
                    {showError("vehicleOverrideReason") && <FieldError>{showError("vehicleOverrideReason")}</FieldError>}
                  </Field>
                </div>
              </>
            )}

            <Field>
              <FieldLabel>Notes</FieldLabel>
              <Textarea
                value={form.notes}
                onChange={(e) => set("notes", e.target.value)}
                rows={2}
                placeholder="Optional notes for reassignment"
              />
            </Field>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose} disabled={reassignMutation.isPending || checkConflictsMutation.isPending}>
            Cancel
          </Button>
          <Button
            variant="outline"
            onClick={async () => {
              setSubmitted(true);
              if (Object.keys(validateReassignForm(form)).length > 0 || !detail) return;
              await runConflictCheck();
            }}
            disabled={detailLoading || checkConflictsMutation.isPending || reassignMutation.isPending}
          >
            {checkConflictsMutation.isPending ? "Checking..." : "Check Conflicts"}
          </Button>
          <Button onClick={handleSubmit} disabled={detailLoading || reassignMutation.isPending || checkConflictsMutation.isPending}>
            {reassignMutation.isPending ? "Saving..." : "Save Reassignment"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
